import { Controller } from "@hotwired/stimulus"

// Global loading queue shared across all instances to prevent thundering herd
const LOAD_QUEUE = {
  pending: [],
  active: 0,
  MAX_CONCURRENT: 3,

  enqueue(callback) {
    if (this.active < this.MAX_CONCURRENT) {
      this.active++
      Promise.resolve()
        .then(() => callback())
        .catch(() => {}) // swallow - frame load errors are non-fatal
        .finally(() => this.onComplete())
    } else {
      this.pending.push(callback)
    }
  },

  onComplete() {
    this.active = Math.max(0, this.active - 1)
    if (this.pending.length > 0 && this.active < this.MAX_CONCURRENT) {
      this.active++
      const next = this.pending.shift()
      Promise.resolve()
        .then(() => next())
        .catch(() => {})
        .finally(() => this.onComplete())
    }
  }
}

// Handles expand/collapse state for objectives and tasks
// Persists state to localStorage for user preference
export default class extends Controller {
  static targets = ["content", "arrow", "trigger", "lazyFrame"]
  static values = {
    storageKey: String,
    defaultExpanded: { type: Boolean, default: false },
    duration: { type: Number, default: 200 }
  }

  connect() {
    // Load saved state from localStorage or use default
    const savedState = localStorage.getItem(this.storageKeyValue)
    this.isExpanded = savedState !== null ? savedState === "true" : this.defaultExpandedValue
    this.frameLoaded = false
    this.frameLoading = false
    this.isVisible = false

    // Apply initial state without animation
    this.updateView(false)

    // Set up IntersectionObserver for viewport-aware lazy loading
    if (this.hasLazyFrameTarget) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.isVisible = true
              this.tryLazyLoad()
            } else {
              this.isVisible = false
            }
          })
        },
        { rootMargin: "300px" }
      )
      this.observer.observe(this.element)
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  toggle(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    this.isExpanded = !this.isExpanded
    this.saveState()
    this.updateView(true)
  }

  expand() {
    if (!this.isExpanded) {
      this.isExpanded = true
      this.saveState()
      this.updateView(true)
    }
  }

  collapse() {
    if (this.isExpanded) {
      this.isExpanded = false
      this.saveState()
      this.updateView(true)
    }
  }

  // Attempt to load the lazy frame if conditions are met (expanded + visible)
  tryLazyLoad() {
    if (!this.isExpanded || !this.isVisible) return
    if (this.frameLoaded || this.frameLoading || !this.hasLazyFrameTarget) return

    const frame = this.lazyFrameTarget
    if (frame.src || !frame.dataset.lazySrc) return

    this.frameLoading = true

    LOAD_QUEUE.enqueue(() => {
      frame.src = frame.dataset.lazySrc
      return new Promise((resolve) => {
        let settled = false

        const onLoad = () => {
          if (settled) return
          settled = true
          clearTimeout(timeoutId)
          frame.removeEventListener("turbo:frame-load", onLoad)
          this.frameLoaded = true
          this.frameLoading = false
          // Disconnect observer after successful load - no longer needed
          if (this.observer) {
            this.observer.disconnect()
            this.observer = null
          }
          resolve()
        }

        frame.addEventListener("turbo:frame-load", onLoad)

        // Safety timeout - allow retry on failure
        const timeoutId = setTimeout(() => {
          if (settled) return
          settled = true
          frame.removeEventListener("turbo:frame-load", onLoad)
          this.frameLoading = false
          // Don't set frameLoaded so retries can occur
          resolve()
        }, 10000)
      })
    })
  }

  updateView(animate = true) {
    if (!this.hasContentTarget) return

    const content = this.contentTarget
    const arrow = this.hasArrowTarget ? this.arrowTarget : null

    // Trigger lazy frame load when expanding (viewport-aware)
    if (this.isExpanded && this.hasLazyFrameTarget) {
      this.tryLazyLoad()
    }

    if (this.isExpanded) {
      // Expand
      if (animate) {
        content.style.display = "block"
        content.style.height = "0px"
        content.style.overflow = "hidden"
        content.classList.remove("hidden")

        // Force a reflow
        content.offsetHeight

        // Animate to full height
        content.style.transition = `height ${this.durationValue}ms ease-in-out`
        content.style.height = content.scrollHeight + "px"

        // Clean up after animation
        setTimeout(() => {
          content.style.height = ""
          content.style.overflow = ""
          content.style.transition = ""
        }, this.durationValue)
      } else {
        content.style.display = "block"
        content.classList.remove("hidden")
      }

      if (arrow) {
        arrow.style.transform = "rotate(180deg)"
      }

      // Update ARIA attributes for accessibility
      if (this.hasTriggerTarget) {
        this.triggerTarget.setAttribute("aria-expanded", "true")
      }
    } else {
      // Collapse
      if (animate) {
        content.style.height = content.scrollHeight + "px"
        content.style.overflow = "hidden"

        // Force a reflow
        content.offsetHeight

        // Animate to 0 height
        content.style.transition = `height ${this.durationValue}ms ease-in-out`
        content.style.height = "0px"

        // Hide after animation
        setTimeout(() => {
          content.classList.add("hidden")
          content.style.display = ""
          content.style.height = ""
          content.style.overflow = ""
          content.style.transition = ""
        }, this.durationValue)
      } else {
        content.classList.add("hidden")
      }

      if (arrow) {
        arrow.style.transform = "rotate(0deg)"
      }

      // Update ARIA attributes for accessibility
      if (this.hasTriggerTarget) {
        this.triggerTarget.setAttribute("aria-expanded", "false")
      }
    }

    // Dispatch custom event for other controllers to listen to
    this.dispatch("toggled", {
      detail: {
        expanded: this.isExpanded,
        element: this.element,
        storageKey: this.storageKeyValue
      }
    })
  }

  saveState() {
    localStorage.setItem(this.storageKeyValue, this.isExpanded.toString())
  }

  // Getter to check if expanded (useful for other controllers)
  get expanded() {
    return this.isExpanded
  }
}
